import { animate, state, style, transition, trigger } from '@angular/animations'
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  inject,
  input,
} from '@angular/core'
import { MixpanelService } from '@src/app/core/services/analytics/mixpanel.service.js'
import { AccordionItemDirective } from './accordion-item.directive.js'

@Component({
  selector: 'app-accordion',
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('contentExpansion', [
      state('expanded', style({ height: '*', opacity: 1, visibility: 'visible' })),
      state(
        'collapsed',
        style({ height: '0px', opacity: 0, visibility: 'hidden', display: 'none' }),
      ),
      transition('expanded <=> collapsed', animate('200ms ease-in')),
    ]),
    trigger('headerIconToggle', [
      state('expanded', style({ transform: 'rotate(0deg)' })),
      state('collapsed', style({ transform: 'rotate(-180deg)' })),
      transition('expanded <=> collapsed', animate('200ms ease-in')),
    ]),
  ],
  standalone: false,
})
export class AccordionComponent implements AfterContentInit {
  private mixPanelService = inject(MixpanelService)

  protected theme = input<AccordionTheme>(AccordionTheme.White)
  protected iconSrc = input<string>('/assets/img/icons/arrow-grey.svg')
  protected firstItemExpanded = input<boolean>(false)

  readonly items = contentChildren(AccordionItemDirective)
  protected renderedItems: readonly AccordionItemDirective[] = []

  ngAfterContentInit(): void {
    this.renderedItems = this.items()

    if (this.firstItemExpanded() && this.renderedItems?.[0]?.id) {
      this.expanded.add(this.renderedItems[0].id)
    }
  }

  protected expanded = new Set<string>()

  protected toggle(id: string): void {
    this.toggleAccordionState(id)
  }

  protected toggleAccordionState(id: string): void {
    if (this.expanded.has(id)) {
      this.expanded.delete(id)
      this.mixPanelService.trackEvent('expandAccordionState')
    } else this.expanded.add(id)
  }

  private iconThemes: Record<AccordionTheme, string> = {
    [AccordionTheme.White]: '/assets/img/icons/arrow-grey.svg',
    [AccordionTheme.Light]: '/assets/img/icons/arrow-purple-border.svg',
    [AccordionTheme.Transparent]: '/assets/img/icons/chevron-purple.svg',
  }

  protected getIconSrc(): string {
    return this.iconThemes[this.theme()]
  }

  protected generateContext(item: AccordionItemDirective): AccordionHeaderContext {
    return {
      $implicit: item,
      id: item.id,
      toggle: () => this.toggleAccordionState(item.id),
    }
  }
}

export enum AccordionTheme {
  White = 'white',
  Light = 'light',
  Transparent = 'transparent',
}

interface AccordionHeaderContext {
  $implicit: AccordionItemDirective
  id: string
  toggle: () => void
}
